import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import images from '../../../assets/images/images';
import styles from './fixedheader.module.scss';
import { ConfigContext } from '../../../providers';
import { staticConfig } from '../../../config/static';
import { CustomLink, Image } from '../..';
import { IRootState } from '../../../redux';
import { cn, isAllowed, isMediaSizeLessThanMD, isTouchScreen } from '../../../helpers';
import { PermissionType } from '../../../enums';
import { Button } from '../../Button/Button';
import { LetterIcon } from '../../../assets/images/svg/lettericon';

interface IFixedHeaderProps {
    title: React.ReactElement;
    menubar?: React.ReactElement;
    mailSort?: React.ReactElement;
    notificationList?: React.ReactElement;
    isMailbox?: boolean;
    onWriteNewMail?: () => void;
}

export const FixedHeader = ({
    title,
    isMailbox,
    onWriteNewMail,
    mailSort,
    menubar,
    notificationList,
}: IFixedHeaderProps): JSX.Element => {
    const { colors } = useContext(ConfigContext);
    const { formatMessage } = useIntl();
    const { scrollTop, mediaSize, access } = useSelector(({ appReadouts, mailbox }: IRootState) => ({
        scrollTop: appReadouts.scrollTop,
        mediaSize: appReadouts.mediaSize,
        access: mailbox.mailbox.access,
    }));

    const isFixed = (): boolean => {
        return !isMediaSizeLessThanMD(mediaSize) && scrollTop >= 200 && !isTouchScreen();
    };

    const handleWriteNewMail = (): void => {
        onWriteNewMail?.();
    };

    const isLessThanMD = isMediaSizeLessThanMD(mediaSize);
    return (
        <>
            {isFixed() && <div className={styles.placeholder} />}
            <section
                className={cn(
                    'mb-md-3',
                    styles.fixedHeader,
                    isFixed() && styles.isFixed,
                    !staticConfig.useNgdp && styles.withTabs
                )}
                style={{ backgroundColor: colors.mailbox.background }}
            >
                {isMailbox && !isLessThanMD && (
                    <div className="d-md-none pt-2 pb-3">
                        <CustomLink
                            disabled={!isAllowed(access, PermissionType.SAVEDRAFT)}
                            className="d-inline-flex align-items-start styleless font-normalizer"
                            onClick={onWriteNewMail}
                            title={formatMessage({ id: 'WRITE_NEW_MESSAGE' })}
                        >
                            <Image src={images.drafts} className="pr-2" />
                            <FormattedMessage id="WRITE_NEW_MESSAGE" />
                        </CustomLink>
                    </div>
                )}

                <div className={`clearfix  ${menubar ? (isFixed() ? 'mb-md-2' : 'mb-md-3') : ''}`}>
                    {!isLessThanMD ? (
                        <h2
                            className={`${styles.title} float-left mw-100 ${menubar && isFixed() ? 'mb-md-2' : ''} ${
                                isMailbox ? 'pl-2 pt-3 p-md-0' : ''
                            }`}
                        >
                            {title}
                        </h2>
                    ) : (
                        <div className="d-flex justify-content-between flex-column pt-3">
                            <h2
                                className={`${styles.title} mb-0
                                    ${menubar && isFixed() ? 'mb-md-4' : ''}
                                    ${isMailbox ? 'px-2 p-md-0' : ''}`}
                            >
                                {title}
                            </h2>
                            {isLessThanMD && (
                                <div className="d-flex flex-column">
                                    {mailSort}
                                    {!isFixed() && notificationList}
                                </div>
                            )}
                        </div>
                    )}

                    {isMailbox && !isLessThanMD && (
                        <div className="d-flex flex-row align-items-start pt-2 float-right">
                            <Button
                                isRounded
                                data-cy="write-new-mail"
                                disabled={!isAllowed(access, PermissionType.SAVEDRAFT)}
                                onClick={handleWriteNewMail}
                                size="small"
                                variant="ghost"
                                startIcon={<LetterIcon />}
                            >
                                <FormattedMessage id="WRITE_NEW_MESSAGE" />
                            </Button>
                        </div>
                    )}
                </div>

                <div className="d-flex flex-column justify-content-between">
                    {menubar}
                    {!isLessThanMD && mailSort}
                    {!isLessThanMD && !isFixed() && notificationList}
                </div>
            </section>
        </>
    );
};
